import { useEffect } from 'react'
import { useEditorStore } from '../store/editorStore'
import { getTotalDurationSec } from '../lib/timelineMath'
import { FRAME_SEC } from '../lib/time'

const SECOND_NUDGE = 1

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? navigator.userAgent)
}

/** ⌘ (Mac) / Ctrl (それ以外) を「修飾キー」として扱う。 */
function hasPrimaryModifier(e: KeyboardEvent): boolean {
  return isMac() ? e.metaKey : e.ctrlKey
}

/**
 * タイムラインのショートカット一式（参照仕様 §3 の表を全部）。
 * Space / ←→ / Delete は既存挙動を維持しつつ、Undo/Redo・分割・複製・吸着・
 * ズーム・Esc 等を追加。input/textarea/contentEditable にフォーカス中は発火しない。
 */
export function useKeyboardShortcuts() {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return
      const store = useEditorStore.getState()
      const primary = hasPrimaryModifier(e)

      if (e.code === 'Space') {
        e.preventDefault()
        store.togglePlaying()
        return
      }

      // ⌘Z / ⇧⌘Z — Undo/Redo は最優先で拾う（他の 'z' 判定より前）
      if (primary && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault()
        if (e.shiftKey) store.redo()
        else store.undo()
        return
      }

      // ⌘B — 分割
      if (primary && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault()
        store.splitAtPlayhead()
        return
      }

      // ⌘D — 複製
      if (primary && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault()
        store.duplicateSelected()
        return
      }

      // ⌘+ / ⌘− / ⌘0 — ズーム
      if (primary && (e.key === '+' || e.key === '=' || e.code === 'Equal')) {
        e.preventDefault()
        store.zoomIn()
        return
      }
      if (primary && (e.key === '-' || e.code === 'Minus')) {
        e.preventDefault()
        store.zoomOut()
        return
      }
      if (primary && e.key === '0') {
        e.preventDefault()
        store.resetZoom()
        return
      }

      // Q / W — 左/右を削除して分割（修飾キー無し。⌘ 付きは無視してブラウザ既定に譲る）
      if (!primary && !e.altKey && (e.key === 'q' || e.key === 'Q')) {
        e.preventDefault()
        store.splitAndKeep('left')
        return
      }
      if (!primary && !e.altKey && (e.key === 'w' || e.key === 'W')) {
        e.preventDefault()
        store.splitAndKeep('right')
        return
      }

      // N — 吸着トグル
      if (!primary && !e.altKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault()
        store.toggleSnap()
        return
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (store.selectedIds.length === 0) return
        e.preventDefault()
        store.removeSelected()
        return
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        store.clearSelection()
        return
      }

      if (e.key === 'Home') {
        e.preventDefault()
        store.setPlaying(false)
        store.setPlayhead(0)
        return
      }
      if (e.key === 'End') {
        e.preventDefault()
        store.setPlaying(false)
        const total = store.composition ? getTotalDurationSec(store.composition) : 0
        store.setPlayhead(total)
        return
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        store.setPlaying(false)
        store.setPlayhead(store.playback.currentSec - (e.shiftKey ? SECOND_NUDGE : FRAME_SEC))
        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        store.setPlaying(false)
        store.setPlayhead(store.playback.currentSec + (e.shiftKey ? SECOND_NUDGE : FRAME_SEC))
        return
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
